import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { canRunExperiment, controlExperiment, getConditionSessions } from '../../../service/experimentService'
import { SessionDataContext } from '../../instructorDashboard/context/SessionDataContext'
import SessionLobby from '../../instructorDashboard/components/SessionLobby'
import { card, cardHeader, cardSubtitle, cardTitle, toolbarButton } from '../../instructorDashboard/components/dashboardStyles'
import { showToast } from '../../commonUnit/Toast'
import { isPythonFamily } from '../sessionSettings'
import { studentPathFor } from '../studentView/studentSurface'
import { codingWindow } from '../../workspace/study/studyConfig'
import { conditionOf, conditionsOutOfStep, experimentConfigOf, sharesOneLink } from './experimentConfig'
import ExperimentOverviewTab from './ExperimentOverviewTab'
import ExperimentPresenterTab from './ExperimentPresenterTab'
import ExperimentCompareTab from './ExperimentCompareTab'
import ExperimentSetupTab from './ExperimentSetupTab'
import ConditionMonitor from './ConditionMonitor'
import { useExperimentPresenter } from './experimentPresenter'

const CONDITION_POLL_MS = 5000
// How long after a study experiment's end the page sends 'expire' itself, if Session
// Control's countdown has not (see the effect in ExperimentPage).
const EXPIRE_BACKSTOP_MS = 5000
// Waits between attempts at canRunExperiment while the server does not answer: a few, then the
// page goes on without knowing (and shows nothing).
const CAN_RUN_RETRY_MS = [2000, 5000, 10000]

// The tabs every instructor who can run the experiment has. Its creator also has a tab per
// condition (that condition's Monitor), Compare conditions and Setup.
const SHARED_TABS = ['control', 'overview', 'presenter']

// Whether the signed-in account created the experiment: the backend's own test (its
// instructors list's canChange) - the row's creater_id against the account's id. Unknown (no
// account yet) is not the creator.
const isCreatorOf = (experimentRow, user) =>
  user?.id != null && experimentRow?.creater_id != null && Number(user.id) === Number(experimentRow.creater_id)

const studentLink = (row) => `${window.location.origin}${studentPathFor(row)}`

const copyLink = (condition) => {
  navigator.clipboard.writeText(studentLink(condition.row))
  showToast(`${condition.name}: student link copied to clipboard!`, 'success', 2000)
}

// Session Control's link card, for a 'By link' experiment: one link per condition. A
// 'Balanced' one keeps the lobby's own card: the experiment's link, which places students.
const ConditionLinksCard = ({ conditions }) => (
  <div style={{ ...card, flexShrink: 0 }}>
    <div style={cardHeader}>
      <h3 style={cardTitle}>Session Links</h3>
    </div>
    <p style={cardSubtitle}>Each condition has its own link. Share each one with the students in that condition.</p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {conditions.map((condition) => (
        <div
          key={condition.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
          }}>
          <span style={{ width: '160px', flexShrink: 0, fontWeight: 600, color: '#111' }}>{condition.name}</span>
          {condition.row ? (
            <>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'Monaco, Menlo, monospace',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#2563eb',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {studentLink(condition.row)}
              </span>
              <button type="button" style={toolbarButton} onClick={() => copyLink(condition)}>
                <ContentCopy sx={{ fontSize: 14 }} />
                Copy
              </button>
            </>
          ) : (
            <span style={{ color: '#9ca3af' }}>Deleted from the session list</span>
          )}
        </div>
      ))}
    </div>
  </div>
)

// The instructor's page for an experiment (/session/:id of its 'Experiment' row), laid out
// like a session's own page (TabPanels): tabs on the left, Distribute and End Session on the
// right.
//   - Session Control: the Monitor's own Session Control (SessionLobby), running every
//     condition together through POST /experiments/:id/control.
//   - Overview: the class as a whole while the exercise runs - progress, errors, AI use, who
//     may need help - with no condition shown and no student's name ('Student N'); a
//     student's detail opens there, not in their condition's tab; the exports. A run's code
//     there goes to the Presenter with Show in Presenter.
//   - Presenter: code to show the class and run against the exercise's tests, laid out as the
//     student page - the instructor's own, or a student's from the Overview, by their Overview
//     label only (ExperimentPresenterTab).
//   - One tab per condition: that condition's own Monitor, unchanged (ConditionMonitor).
//   - Compare conditions: a card per condition, every student in one table with their
//     condition, the exports (the Overview before it became the class view).
//   - Setup: what was published, and who else can run the experiment.
// Each condition stays an ordinary session, with its own page and Session Control.
//
// Only the experiment's creator (its row's creater_id) has every tab. An instructor the creator
// added (Setup's instructors), such as the course's instructor running the exercise in class,
// has Session Control, the Overview - without Compare conditions or the exports, whose files
// name each student's condition - and the Presenter: nothing that tells the conditions apart,
// except, in an experiment with a link per condition ('link'), Session Control's links card and
// the Distribute menu, which name each condition's link. The creator adds accounts, not people:
// signed in with an account that is not on the list, an instructor is told so above Session
// Control (the server refuses its Start), and a TA that only instructors can run experiments.
const ExperimentPage = () => {
  const { session: experimentRow, setSession } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext) || {}
  const isCreator = isCreatorOf(experimentRow, currentUser)
  const config = experimentConfigOf(experimentRow)
  // Whether the server lets this account run the experiment, asked once the account is known
  // for anyone but its creator: false only on its refusal (403, also from an action below),
  // and true again once an action goes through (the creator added the account meanwhile);
  // null while unknown, and when the server did not answer - the page then works as before.
  const [canRun, setCanRun] = useState(null)
  // A TA (role 2) is refused whatever the list says: the server runs experiments for
  // instructors only, and adds only their accounts.
  const isTA = Number(currentUser?.role) === 2
  const experimentId = experimentRow?.id
  const userId = currentUser?.id
  useEffect(() => {
    setCanRun(null)
    if (isCreator || userId == null || experimentId == null) return undefined
    let cancelled = false
    let timer = null
    const attempt = async (tries) => {
      const answer = await canRunExperiment(experimentId)
      if (cancelled) return
      if (answer !== null) setCanRun(answer)
      else if (tries < CAN_RUN_RETRY_MS.length) timer = setTimeout(() => attempt(tries + 1), CAN_RUN_RETRY_MS[tries])
    }
    attempt(0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [experimentId, isCreator, userId])
  const [conditionRows, setConditionRows] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [tab, setTab] = useState('control')
  const [focusRequest, setFocusRequest] = useState(null)
  const [distributeAnchor, setDistributeAnchor] = useState(null)
  const [confirmEnd, setConfirmEnd] = useState(false)
  // Bumped after every all-conditions action, so an open condition Monitor remounts and its
  // Session Control re-reads the row instead of running on with its own local timer.
  const [controlVersion, setControlVersion] = useState(0)
  // The Presenter's list and editor, here so the Overview can add to them; kept in this
  // browser per experiment.
  const presenter = useExperimentPresenter(experimentRow?.id)
  // The tab shown: one this instructor has, else Session Control.
  const shownTab = isCreator || SHARED_TABS.includes(tab) ? tab : 'control'
  // The Presenter mounts when first opened and then stays (hidden on the other tabs), so its
  // Python runtime is loaded once and its output kept.
  const [presenterOpened, setPresenterOpened] = useState(false)
  useEffect(() => {
    if (shownTab === 'presenter') setPresenterOpened(true)
  }, [shownTab])

  const idsKey = (config?.conditions || []).map((c) => c.sessionId).filter(Boolean).join(',')

  // Keep a row's object while nothing in it changed, so the scoped contexts under a
  // condition's Monitor do not change on every poll.
  const rowsRef = useRef([])
  const loadConditions = useCallback(async () => {
    if (!idsKey) return
    try {
      const fresh = await getConditionSessions(idsKey.split(',').map(Number))
      const previous = new Map(rowsRef.current.map((row) => [row.id, row]))
      const merged = fresh.map((row) => {
        const old = previous.get(row.id)
        return old && JSON.stringify(old) === JSON.stringify(row) ? old : row
      })
      rowsRef.current = merged
      setConditionRows(merged)
      setLoadError('')
    } catch (error) {
      setLoadError('Could not load the condition sessions. Retrying…')
    }
  }, [idsKey])

  useEffect(() => {
    loadConditions()
    const timer = setInterval(loadConditions, CONDITION_POLL_MS)
    return () => clearInterval(timer)
  }, [loadConditions])

  // Each configured condition with its session row: only a row that still points back at this
  // experiment and was not deleted from the session list counts.
  const conditions = useMemo(
    () =>
      (config?.conditions || []).map((condition) => ({
        ...condition,
        row:
          (conditionRows || []).find(
            (row) =>
              !row.is_deleted &&
              Number(row.id) === Number(condition.sessionId) &&
              conditionOf(row, experimentRow)?.key === condition.key
          ) || null,
      })),
    [config, conditionRows, experimentRow]
  )
  const liveRows = conditions.map((c) => c.row).filter(Boolean)

  // `remount`: whether an open condition Monitor remounts (see controlVersion).
  const handleChanged = ({ experiment, conditions: rows }, { remount = true } = {}) => {
    if (experiment) setSession(experiment)
    if (rows?.length) {
      const updated = (rowsRef.current || []).map((row) => rows.find((r) => r.id === row.id) || row)
      rowsRef.current = updated
      setConditionRows(updated)
    }
    if (!remount) return
    setFocusRequest(null)
    setControlVersion((n) => n + 1)
  }

  // One action for every condition. `quiet`: an automatic action another open copy of this
  // page may already have done. `remount: false` keeps an open condition Monitor as it is.
  const act = async (action, extra = {}, { quiet = false, remount = true } = {}) => {
    const result = await controlExperiment(experimentRow.id, action, extra)
    if (!result.ok) {
      // Refused for this account: said above Session Control too, where it stays until an
      // action goes through.
      if (result.status === 403 && !isCreator) setCanRun(false)
      if (!quiet) showToast(result.message, 'error', 4000)
      return false
    }
    setCanRun((value) => (value === false ? true : value))
    handleChanged(result, { remount })
    return true
  }

  // A study experiment's coding time must end even when Session Control's countdown misses
  // the moment: a poll that re-syncs the lobby just after the time ran out stops its
  // countdown without sending 'expire'. So while the page watches a running study
  // experiment, it also expires it a few seconds after the end. When the lobby did first,
  // the row is 'closed' by then and the timer is cleared (a late second request is a quiet 409).
  const studyEndMs =
    config?.study && experimentRow?.status === 'active' ? codingWindow(experimentRow)?.endsAtMs ?? null : null
  useEffect(() => {
    // Only an end still ahead. A row first seen past its end - a stale poll answer, or an
    // experiment left running earlier - is left to the instructor, as before.
    if (studyEndMs == null || studyEndMs <= Date.now()) return undefined
    const timer = setTimeout(
      () => act('expire', {}, { quiet: true, remount: false }),
      studyEndMs - Date.now() + EXPIRE_BACKSTOP_MS
    )
    return () => clearTimeout(timer)
  }, [studyEndMs]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!config) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">This experiment has no saved settings.</Alert>
      </Box>
    )
  }

  const scope = `all ${conditions.length} conditions`
  const oneLink = sharesOneLink(experimentRow)
  const lobbyController = {
    start: (minutes) => act('start', { durationMinutes: minutes }),
    resume: () => act('resume'),
    stop: () => act('pause'),
    reset: () => act('reset'),
    setDuration: (minutes) => act('duration', { durationMinutes: minutes }, { quiet: true }),
    // Fires on whichever tab is open (Session Control stays mounted, see below). An open
    // condition's Monitor is not remounted for it: its own Session Control reaches zero at
    // the same moment, and the instructor keeps their place in it.
    expire: () => act('expire', {}, { quiet: true, remount: false }),
    studentCount: liveRows.reduce((sum, row) => sum + (row.active_stu || 0), 0),
    pausedSeconds: config.control?.pausedSeconds ?? null,
    linkCard: oneLink ? null : <ConditionLinksCard conditions={conditions} />,
    scope,
  }
  const outOfStep = conditionRows ? conditionsOutOfStep(experimentRow, liveRows) : []
  const ended = experimentRow?.closeSessionType === 'closed with survey'
  const lobbyNotice = (
    <>
      {canRun === false &&
        (isTA ? (
          <Alert severity="warning">
            <strong>Only instructors can run experiments.</strong> Start, Pause and End will not work for a TA account.
          </Alert>
        ) : (
          <Alert severity="warning">
            <strong>This account cannot run this experiment.</strong> Start, Pause and End will not work for it. Sign in
            with the account the experiment&apos;s creator added, or ask the creator to add this account, then reload
            this page.
          </Alert>
        ))}
      {oneLink && (
        <Alert severity="info">
          Every student uses the link below: each one is placed in a condition when they first open it, so the groups
          stay the same size.
        </Alert>
      )}
      {ended && (
        <Alert severity="warning">
          The session has ended: AI chat is off in every condition and students see the end-of-session survey.
          Start or Reset turns each condition&apos;s AI chat back to its published setting.
        </Alert>
      )}
      {outOfStep.length > 0 && (
        <Alert severity="warning">
          {outOfStep.length === 1 ? 'One condition is' : `${outOfStep.length} conditions are`} out of step, because
          its own Session Control was used. The next action here brings every condition back in step.
        </Alert>
      )}
    </>
  )

  const openStudent = (key, studentId) => {
    setFocusRequest({ studentId, nonce: Date.now() })
    setTab(key)
  }
  // From the Overview: the code into the Presenter's list and editor, and the Presenter open.
  const showInPresenter = (entry) => {
    presenter.add(entry)
    setFocusRequest(null)
    setTab('presenter')
  }
  const current = isCreator ? conditions.find((condition) => condition.key === shownTab) : null
  // Every condition runs the same exercise: the Presenter shows a condition's task and tests,
  // and runs Python only.
  const exercise = conditions.find((condition) => condition.row)?.row || null
  const exerciseType = exercise?.type || null

  return (
    // The tab bar stays put and the tab's content scrolls under it. (With the session page's
    // own overflowY: hidden, anything taller than the window was cut off with no way to reach it.)
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <Tabs
          value={shownTab}
          onChange={(event, value) => {
            setFocusRequest(null)
            setTab(value)
          }}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Experiment tabs">
          <Tab value="control" label="Session Control" />
          <Tab value="overview" label="Overview" />
          <Tab value="presenter" label="Presenter" />
          {isCreator &&
            conditions.map((condition) => (
              <Tab key={condition.key} value={condition.key} label={condition.name} disabled={!condition.row} />
            ))}
          {isCreator && <Tab value="compare" label="Compare conditions" />}
          {isCreator && <Tab value="setup" label="Setup" />}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Typography sx={{ color: '#6b7280', fontSize: '14px', mr: 1 }} noWrap>
            <strong>{config.name}</strong>
          </Typography>
          <Button
            sx={{ marginRight: '20px', marginLeft: '20px' }}
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={(event) => {
              if (!oneLink) {
                setDistributeAnchor(event.currentTarget)
                return
              }
              navigator.clipboard.writeText(studentLink(experimentRow))
              showToast('Link copied to clipboard!', 'success', 2000)
            }}>
            Distribute
          </Button>
          <Menu anchorEl={distributeAnchor} open={Boolean(distributeAnchor)} onClose={() => setDistributeAnchor(null)}>
            {conditions.map((condition) => (
              <MenuItem
                key={condition.key}
                disabled={!condition.row}
                onClick={() => {
                  setDistributeAnchor(null)
                  copyLink(condition)
                }}>
                Copy the link for {condition.name}
              </MenuItem>
            ))}
          </Menu>
          <Button
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              marginRight: '20px',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
            onClick={() => setConfirmEnd(true)}>
            End Session
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {/* Not over the Presenter: it fills the page's height, and keeps the last rows it had. */}
      {loadError && shownTab !== 'presenter' && (
        <Alert severity="error" sx={{ m: 2 }}>
          {loadError}
        </Alert>
      )}

      {/* Kept mounted (hidden) while another tab is open, like the Monitor keeps its own
          (InstructorDashboardPanel): its countdown is what sends 'expire' when the time runs
          out, and by then the instructor is often on the Overview or a condition's tab.
          display: contents when shown leaves the layout exactly as without the wrapper (the
          lobby's height: 100% still measures the scroll area). */}
      {conditionRows && (
        <div style={{ display: shownTab === 'control' ? 'contents' : 'none' }}>
          <SessionDataContext.Provider value={{ sessionId: experimentRow.id, session: experimentRow }}>
            <SessionLobby sessionId={experimentRow.id} notice={lobbyNotice} controller={lobbyController} />
          </SessionDataContext.Provider>
        </div>
      )}
      {shownTab === 'overview' && conditionRows && (
        <ExperimentOverviewTab
          experimentId={experimentRow.id}
          config={config}
          conditions={conditions}
          onOpenCompare={isCreator ? () => setTab('compare') : undefined}
          withExports={isCreator}
          onShowInPresenter={showInPresenter}
        />
      )}
      {(presenterOpened || shownTab === 'presenter') && (
        <div style={{ display: shownTab === 'presenter' ? 'contents' : 'none' }}>
          <ExperimentPresenterTab
            key={experimentRow.id}
            presenter={presenter}
            exercise={exercise}
            runnable={!exerciseType || isPythonFamily(exerciseType)}
            exerciseType={exerciseType}
          />
        </div>
      )}
      {isCreator && shownTab === 'compare' && conditionRows && (
        <ExperimentCompareTab
          experimentId={experimentRow.id}
          config={config}
          conditions={conditions}
          onOpenCondition={setTab}
          onOpenStudent={openStudent}
        />
      )}
      {current?.row && (
        <ConditionMonitor
          key={`${current.row.id}-${controlVersion}`}
          condition={current}
          row={current.row}
          focusRequest={focusRequest}
          onFocusHandled={() => setFocusRequest(null)}
        />
      )}
      {isCreator && shownTab === 'setup' && (
        <ExperimentSetupTab experimentId={experimentRow.id} config={config} conditions={conditions} />
      )}
      </Box>

      <Dialog open={confirmEnd} onClose={() => setConfirmEnd(false)} aria-labelledby="xp-end-title">
        <DialogTitle id="xp-end-title">End the session for {scope}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            AI chat turns off in every condition, and students in Python conditions get the end-of-session survey.
            Their code stays open. Start or Reset in Session Control turns the chat back on.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEnd(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setConfirmEnd(false)
              if (await act('end')) showToast(`Session ended for ${scope}.`, 'success', 2500)
            }}>
            End Session
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ExperimentPage
